import { apiBearerAI } from '~/services/api';
import Notify from '~/components/Notify/Notify';
import { AIKnowledgeSyncJob, AISectionSyncAction } from './AIChatbotModel';

const SESSION_STORAGE_KEY = 'ai-knowledge-sync-jobs';
export const AI_SECTION_SYNC_EVENT = 'ai-section-sync-jobs-updated';
const INDEXABLE_SECTION_TYPES = new Set(['text', 'video', 'podcast', 'survey']);

export type AISectionSyncRequest = {
  courseId: number;
  sectionId: number;
  action: AISectionSyncAction;
};

export const isAIIndexableSectionType = (type?: string) =>
  !!type && INDEXABLE_SECTION_TYPES.has(type);

export const getAISectionSyncJob = async (id: number) => {
  const response = await apiBearerAI({}).get<AIKnowledgeSyncJob>(
    `/ai/api/v1/knowledge/sync-jobs/${id}`
  );
  return response.data;
};

type PendingAISectionSyncJob = Pick<
  AIKnowledgeSyncJob,
  'id' | 'course_id' | 'section_id' | 'action'
> & {
  attempts: number;
  kind: 'section' | 'course';
};

export const getPendingAISectionSyncJobs = (): PendingAISectionSyncJob[] => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
};

export const setPendingAISectionSyncJobs = (
  jobs: PendingAISectionSyncJob[]
) => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(jobs));
  window.dispatchEvent(new Event(AI_SECTION_SYNC_EVENT));
};

export const hasPendingAICourseSync = (courseId: number) =>
  getPendingAISectionSyncJobs().some(
    (job) => job.course_id === courseId && job.kind === 'course'
  );

const addPendingAIKnowledgeSyncJobs = (
  jobs: AIKnowledgeSyncJob[],
  kind: PendingAISectionSyncJob['kind']
) => {
  const pending = getPendingAISectionSyncJobs();
  const next = [...pending];
  jobs.forEach((job) => {
    if (!next.some((pendingJob) => pendingJob.id === job.id)) {
      next.push({
        id: job.id,
        course_id: job.course_id,
        section_id: job.section_id,
        action: job.action,
        attempts: 0,
        kind,
      });
    }
  });
  setPendingAISectionSyncJobs(next);
};

export const enqueueAISectionSync = async ({
  courseId,
  sectionId,
  action,
}: AISectionSyncRequest) => {
  try {
    const response = await apiBearerAI({}).post<AIKnowledgeSyncJob>(
      '/ai/api/v1/knowledge/section-events',
      { course_id: courseId, section_id: sectionId, action }
    );
    addPendingAIKnowledgeSyncJobs([response.data], 'section');
  } catch {
    Notify.warning({
      message:
        'Section was saved, but the AI knowledge sync could not be queued.',
    });
  }
};

export const enqueueAICourseSync = async (courseId: number) => {
  try {
    const response = await apiBearerAI({}).post<AIKnowledgeSyncJob[]>(
      '/ai/api/v1/knowledge/sync',
      { course_ids: [courseId] }
    );
    addPendingAIKnowledgeSyncJobs(response.data, 'course');
    return true;
  } catch {
    Notify.error({
      message: 'AI course sync could not be queued. Please try again.',
    });
    return false;
  }
};

export type { PendingAISectionSyncJob };
